import { useMemo, useState } from "react";
import mixpanel from "mixpanel-browser";
import { validatePromoCode } from "api/subscriptions/promoApi";
import Toaster from "components/shared/Toaster";

interface Package {
  id: number;
  name: string;
  cost: number;
  valid_duration: string;
  credits: number;
}

interface Promo {
  id: number;
  type: string;
  code: string;
  discount: number;
  promo: boolean;
  valid_until?: number;
}

const usePurchase = (packagePlan) => {
  const [promo, setPromo] = useState<Promo>();
  const [selectedPackage, setSelectedPackage] = useState<Package>(packagePlan);
  // const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);

  // const price = useMemo(() => {
  //   const num = Number(packagePlan?.cost) * quantity;
  //   return num;
  // }, [packagePlan, quantity]);

  const pricingDetails = useMemo(() => {
    const originalPrice = selectedPackage?.cost;
    const discountPercentage = promo?.discount || 0;
    const discountedAmount = (originalPrice * discountPercentage) / 100;
    const netPrice = originalPrice - discountedAmount;
    const transactionFee = netPrice > 0 ? netPrice * 0.03 + 1 : 0;
    const subTotal = netPrice + transactionFee;

    return { originalPrice, discountedAmount, transactionFee, subTotal };
  }, [promo, selectedPackage]);

  const handlePackageChange = (selectedPackage) => {
    setSelectedPackage(selectedPackage);
    setPromo(null);
  };

  const trackCheckoutButton = () => {
    mixpanel.track("Clicked Checkout Button", {
      "Credit Quantity": 1,
      "Package ID": selectedPackage.id,
    });
  };

  // FUT-1596: handleSubmit function for PurchasePage has to be a form submission
  // as a temp solution to allow rails to handle the redirect.
  // Using axios will result in a CORS preflight request, which could lead to
  // CORS issues when the browser tries to follow the redirect to Stripe.
  // Consider refactoring in future.
  const handleSubmit = async (userId, actionPath) => {
    setLoading(true);

    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      .getAttribute("content");

    const data = {
      authenticity_token: csrfToken,
      user_subscription: {
        package_id: selectedPackage.id,
        user_id: userId,
        quantity: 1,
        promo_code_id: promo?.id,
      },
    };

    const form = document.createElement("form");

    // Set form attributes
    form.method = "POST";
    form.action = actionPath;

    const appendHiddenFields = (formData, parentKey = "") => {
      Object.keys(formData).forEach((key) => {
        const value = formData[key];
        const inputName = parentKey ? `${parentKey}[${key}]` : key;

        if (typeof value === "object" && value !== null) {
          // Recursively append nested objects
          appendHiddenFields(value, inputName);
        } else {
          // Create hidden input for non-object values
          const hiddenField = document.createElement("input");
          hiddenField.type = "hidden";
          hiddenField.name = inputName;
          hiddenField.value = value;
          form.appendChild(hiddenField);
        }
      });
    };

    // Append the nested form data to the form
    appendHiddenFields(data);

    // Add the form to the document body
    document.body.appendChild(form);

    // Submit the form
    form.submit();

    trackCheckoutButton();
  };

  return {
    setSelectedPackage,
    selectedPackage,
    pricingDetails,
    handlePromoCode: (value) => setPromo(value),
    handlePackageChange,
    handleSubmit,
    promo,
    loading,
    setPromo,
  };
};

export default usePurchase;
